
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Database } from "@/types/supabase";
import { logger } from "@/lib/services/logger-service";
import { useChildStore } from "@/lib/store/child";

type Child = Database["public"]["Tables"]["children"]["Row"];

export function useEditChildForm(
  selectedChild: Child | null,
  onSuccess?: (updatedChild: Child) => void
) {
  const [name, setName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Date>();
  const { toast } = useToast();
  const { setChildren } = useChildStore();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form values when selected child changes
  useEffect(() => {
    if (selectedChild) {
      setName(selectedChild.name);
      
      // Parse the date without timezone adjustments
      if (selectedChild.date_of_birth) {
        const dateObj = new Date(selectedChild.date_of_birth);
        logger.debug('Parsed date of birth for editing', {
          originalISOString: selectedChild.date_of_birth,
          parsedDate: dateObj.toISOString(),
          localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });
        setDateOfBirth(dateObj);
      } else {
        setDateOfBirth(undefined);
      }
    }
  }, [selectedChild]);

  const handleSave = () => {
    if (!selectedChild) return;

    if (!dateOfBirth) {
      logger.error('Missing date of birth during edit save');
      return;
    }

    setIsSubmitting(true);

    try {
      // Set the time to noon UTC (12:00:00) to prevent date shifting across time zones
      const dateAtNoonUTC = new Date(dateOfBirth);
      dateAtNoonUTC.setUTCHours(12, 0, 0, 0);
      
      // Format as ISO string for consistent database storage
      const formattedDate = dateAtNoonUTC.toISOString();
      
      logger.debug('Updated birthdate with noon UTC approach', {
        originalDate: dateOfBirth.toISOString(),
        noonUTCDate: formattedDate,
        localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        tzOffset: new Date().getTimezoneOffset()
      });

      const updatedChild: Child = {
        ...selectedChild,
        name: name,
        date_of_birth: formattedDate
      };

      if (onSuccess) {
        onSuccess(updatedChild);
      }

      toast({
        title: "Success",
        description: "Child information updated successfully",
      });
    } catch (error) {
      logger.error('Error saving child changes:', { error });
      toast({
        title: "Error",
        description: "Failed to update child information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    if (selectedChild) {
      setName(selectedChild.name);
      if (selectedChild.date_of_birth) {
        setDateOfBirth(new Date(selectedChild.date_of_birth));
      }
    }
  };

  return {
    name,
    setName,
    dateOfBirth,
    setDateOfBirth,
    isSubmitting,
    handleSave,
    resetForm
  };
}
